/**
 * Custom hook untuk mengambil detail booking berdasarkan ID.
 * 
 * API Endpoint: GET `/api/availability`
 * API Version: 3.0
 * 
 * @param {string} gte - Tanggal checkin
 * @param {string} lte - Tanggal checkout
 * @returns {object} - Return value berupa object dengan key `data`, `isLoading`, dan `error`.
 * @example
 * const { data, isLoading, error } = useAvailability({ gte: '2022-01-01', lte: '2022-01-02' });
 */

import { useQuery } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { AvailabilityResponseSchema } from "../response/AvailabilityResponse";

const API_VERSION = 'v3';

interface AvailabilityProps {
    gte: string;
    lte: string;
}

const useAvailability = ({ gte, lte }: AvailabilityProps) => {
    return useQuery({
        queryKey: ['bookings', 'booking',  'monthly', 'calendar', 'availability', gte, lte],
        queryFn: async () => {
            try {
                const response = await privateApi.get(`/${API_VERSION}/availability`, {
                    params: {
                        filter: {
                            date: {
                                gte,
                                lte,
                            }
                        }
                    }
                });

                // Validasi response
                const validationResult = AvailabilityResponseSchema.safeParse(response.data);
                if (!validationResult.success) {
                    console.error("Validation failed:", validationResult.error.errors);
                    throw new Error("Invalid Availability data format");
                }
                return validationResult.data;
            } catch (error) {
                console.error(`Failed to fetch Availability`, error);
                throw error;
            }
        },
        enabled: !!gte && !!lte,
        retry: import.meta.env.VITE_MAX_RETRY,
        refetchOnWindowFocus: false,
    });
};

export default useAvailability;